// src/lib/planning/periodPage.ts
//
// The pure model behind the three planning pages — This Month, This Season,
// This Year. One shape, learned once (Scott, 2026-09-05): the level's own list,
// a rail with the level above, and a look-back at the period just ended.
// Nothing here renders; the pages ask these questions and draw the answers.

import type { Task } from '@/types/task'
import type { Seasons } from '@/lib/cadence/seasons'
import { seasonStartFor, seasonEndFor, seasonLabel, readSeasons } from '@/lib/cadence/seasons'
import { monthStartOf } from './periodPlacement'
import { committedTo, type PlacementFate } from '@/lib/placement/model'
import { doableBy } from './poolViews'

export type PlanLevel = 'month' | 'season' | 'year'

export interface PeriodBounds {
  level: PlanLevel
  /** Inclusive start, midnight. */
  start: Date
  /** Exclusive end, midnight of the next period's first day. */
  end: Date
  label: string
  /** Start of the previous / next period — the navigator's arrows. */
  prev: Date
  next: Date
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** The period containing `anchor`, at `level`. Seasons follow the household's
 *  configured boundaries; months and years are calendar. */
export function periodBounds(level: PlanLevel, anchor: Date, seasons: Seasons): PeriodBounds {
  if (level === 'month') {
    const start = monthStartOf(anchor)
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1)
    const prev = new Date(start.getFullYear(), start.getMonth() - 1, 1)
    return { level, start, end, label: `${MONTHS[start.getMonth()]} ${start.getFullYear()}`, prev, next: end }
  }
  if (level === 'season') {
    const start = seasonStartFor(anchor, seasons)
    const end = seasonEndFor(anchor, seasons)
    const dayBefore = new Date(start); dayBefore.setDate(dayBefore.getDate() - 1)
    const prev = seasonStartFor(dayBefore, seasons)
    return { level, start, end, label: seasonLabel(anchor, seasons), prev, next: end }
  }
  const start = new Date(anchor.getFullYear(), 0, 1)
  const end = new Date(anchor.getFullYear() + 1, 0, 1)
  return { level, start, end, label: `${start.getFullYear()}`, prev: new Date(anchor.getFullYear() - 1, 0, 1), next: end }
}

export function isCurrentPeriod(bounds: PeriodBounds, today: Date): boolean {
  return today >= bounds.start && today < bounds.end
}

export interface PlanningPeriodInput {
  level: PlanLevel
  today: Date
  seasons: Seasons
  explicitStart?: Date | null
}

/** The period a planning page should show first: an explicit start (from the
 *  URL) always wins; otherwise the current period — unless it is nearly over
 *  (≤14 days left for a season, ≤6 for a month), in which case the page opens
 *  on the coming period. A year page never looks ahead.
 *
 *  There used to be a second rule: if the current period was empty and the next
 *  had a list, the page silently opened on the next one (demo run 2026-09-06).
 *  It was removed on 2026-09-23. It did not fire when Scott needed it — he
 *  landed on an empty September with his October plan invisible — and a jump
 *  that depends on data makes the page you arrive at unpredictable. The page
 *  now stays where the clock says and NAMES the neighbour that holds the plan
 *  ("October has 1 goal and 2 tasks →"), which keeps orientation and leaves the
 *  move to the reader. See `neighbourWithWork` in `PeriodPlanPage`. */
export function planningPeriod({ level, today, seasons, explicitStart }: PlanningPeriodInput): { start: Date; lookingAhead: boolean } {
  if (explicitStart) return { start: periodBounds(level, explicitStart, seasons).start, lookingAhead: false }
  const cur = periodBounds(level, today, seasons)
  if (level === 'year') return { start: cur.start, lookingAhead: false }
  const daysLeft = Math.round((cur.end.getTime() - today.getTime()) / 86_400_000)
  const threshold = level === 'season' ? 14 : 6
  const nextStart = cur.next
  if (daysLeft <= threshold) return { start: nextStart, lookingAhead: true }
  return { start: cur.start, lookingAhead: false }
}

/**
 * Does a goal, or a step of one, stay on the plan when it is assigned only to
 * other people? A household goal is the household's plan: yes, unless it is
 * private. Scott, 2026-09-25: "if they're assigned with a personal context,
 * then no they should not stay visible on my page". Work is private in the
 * same way (Personal and Work are private; Family is shared). A step with no
 * area of its own is read by its goal's — a step inherits its goal's domain,
 * as `updateTask` derives it. Plain tasks are not covered: someone else's
 * task still leaves your list (the 2026-09-05 rule).
 */
export function staysOnSharedPlan(t: Task, byId: ReadonlyMap<string, Task>): boolean {
  const goal = t.isGoal ? t : t.goalTaskId ? byId.get(t.goalTaskId) : undefined
  if (!goal?.isGoal) return false
  const area = t.context ?? goal.context ?? null
  return area !== 'personal' && area !== 'work'
}

/**
 * The tasks on a month or season list. The CURRENT period is a pool question
 * (`belongsTo*` — a legacy NULL row is this period's); any other period is a
 * membership question (`isPlacedOn*` — or a NULL row would repeat in every
 * period you page to). Scoped to `meId` the way the week strip and the Month
 * rail are: unassigned and mine stay, someone else's goes — except a shared
 * goal and its steps, which stay (`staysOnSharedPlan`). Completed and placed
 * rows are INCLUDED — the list is the record, and the look-back needs them.
 * Year lists are goals, not tasks; see the page.
 */
/**
 * A completion with no time at all: done, with no completed_at, no day, no
 * period stamp and no commitment (legacy rows such as bucket 'month' with no
 * month_start). The legacy rule would file it on the CURRENT period, so every
 * month read "24 of 36 done" from old work (visual check, 2026-11-04). Its
 * time is unknown, so it is on no period's list, fold or count; search still
 * finds it.
 */
export function isUndatedLegacyDone(t: Pick<Task, 'completed' | 'completedAt' | 'scheduledFor' | 'weekStart' | 'monthStart' | 'seasonStart' | 'commitments'>): boolean {
  return !!t.completed && !t.completedAt && !t.scheduledFor && !t.weekStart && !t.monthStart && !t.seasonStart && !(t.commitments?.length)
}

export function selectPeriodTasks(
  tasks: readonly Task[],
  level: 'month' | 'season',
  start: Date,
  isCurrent: boolean,
  meId: string | null,
  seasons: Seasons = readSeasons(),
): Task[] {
  // One enduring action: the list is every row COMMITTED to this period,
  // whatever else it carries — a season item taken into September is on the
  // season list and the month list; a month item dated Wednesday is still on
  // the month list, marked "→ Wednesday". A row with no records answers from
  // its cached bucket + stamp the way it always did (the NULL rule).
  const byId = new Map(tasks.map((t) => [t.id, t]))
  return tasks
    .filter((t) => {
      if (meId && !doableBy(t, meId) && !staysOnSharedPlan(t, byId)) return false
      if (isUndatedLegacyDone(t)) return false
      return committedTo(t, level, start, { isCurrent, seasons }) !== undefined
    })
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
}

/**
 * Timed items landing inside the period — the ones that showed up on Today
 * but never appeared on the month/season page they were also inside (demo
 * run 2026-09-06: six dated September items were invisible on /month, which
 * only ever asked the pool question). Not completed, sorted chronologically
 * with an all-day item ranked before a timed one on the same day.
 */
export function selectDatedInPeriod(tasks: readonly Task[], bounds: PeriodBounds): Task[] {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return tasks
    .filter((t): t is Task & { scheduledFor: Date } => !t.completed && !!t.scheduledFor && t.scheduledFor >= bounds.start && t.scheduledFor < bounds.end)
    .sort((a, b) => {
      const dayDiff = startOfDay(a.scheduledFor) - startOfDay(b.scheduledFor)
      if (dayDiff !== 0) return dayDiff
      if (!!a.isAllDay !== !!b.isAllDay) return a.isAllDay ? -1 : 1
      return a.scheduledFor.getTime() - b.scheduledFor.getTime()
    })
}

export type RowAction =
  | 'complete' | 'keep' | 'someday' | 'drop' | 'make-goal' | 'make-task'
  /** Take this row down a rung — month → this week, season → this month. The
   *  motion the whole cadence rests on (Scott: "we reference the monthly list
   *  to decide what to do for the week"), which the page had no way to do:
   *  the rail could pull DOWN into the page, but a row on the page itself
   *  could not go anywhere. */
  | 'to-lower'
  /** Straight onto today. An urgent thing shouldn't have to walk every rung. */
  | 'today'
  /** File a loose row under one of the period's goals. You write "call the
   *  roofer" and only then realise it is porch work. */
  | 'under-goal'
  /** Take a next action out from under its goal. The task stays exactly
   *  where it is — dated, committed, done or not — only the link goes. */
  | 'off-goal'

/**
 * The verbs a row offers.
 *
 * In the current period a TASK can be ticked, taken down a rung, taken
 * straight to today, converted, or dropped. Once the period is over it gets
 * its look-back fate instead: keep (copy forward), drop, Someday (tasks only
 * — a goal is an outcome, not a thing you postpone), or change kind.
 *
 * A GOAL is never placed. It is ticked, kept or dropped, and every placement
 * writer refuses it anyway (periodPlacement's is_goal refusal) — so offering
 * it a rung would be offering a no-op.
 *
 * Done rows are the win column and offer nothing. A row already placed lower
 * can still be kept or dropped in a look-back — its copy carries on
 * regardless — but it is not re-placed from here, which is what kept ten
 * copies of the same task from landing in twenty seconds.
 */
export function actionsFor(
  { fate, isGoal, isPast, level = 'month', hasGoals = false, isStep = false, elsewhere = false }:
  { fate: PlacementFate; isGoal: boolean; isPast: boolean; level?: PlanLevel; hasGoals?: boolean
    /** A next action drawn under its goal: it can be taken out from under it. */
    isStep?: boolean
    /** A step that is not on this page's own list: only ticked or taken out
     *  from here — its placement belongs to the list it is on. */
    elsewhere?: boolean },
): RowAction[] {
  // A look-back is read, not rewired; otherwise any step may leave its goal.
  const offGoal: RowAction[] = isStep && !isGoal && !isPast ? ['off-goal'] : []
  if (elsewhere) return fate === 'done' || fate === 'placed-done' ? offGoal : [...(isPast ? [] : ['complete' as const]), ...offGoal]
  if (fate === 'done' || fate === 'placed-done') return offGoal
  if (fate === 'placed-open') return isPast ? ['keep', 'drop'] : ['complete', ...(!isGoal && hasGoals && !isStep ? ['under-goal' as const] : []), ...offGoal]
  const kind: RowAction[] = [] // Goals guide actions; planning never converts their identity.
  // A year row is a goal entity and has no rung below it on this page.
  const canDescend = !isGoal && level !== 'year'
  // Only a loose row, and only where there is a goal to file it under. A goal
  // never goes under a goal: one level, on purpose.
  const underGoal: RowAction[] = !isGoal && hasGoals && !isStep ? ['under-goal'] : []
  if (!isPast) {
    return canDescend
      ? ['complete', 'to-lower', 'today', ...underGoal, ...offGoal, ...kind, 'drop']
      : ['complete', ...underGoal, ...offGoal, ...kind, 'drop']
  }
  return isGoal ? ['complete', 'keep', ...kind, 'drop'] : ['complete', 'keep', 'someday', ...kind, 'drop']
}

/** The rung a row drops into from this page. */
export function lowerLevel(level: PlanLevel): 'week' | 'month' | null {
  if (level === 'month') return 'week'
  if (level === 'season') return 'month'
  return null
}

/** The level a page looks at while it plans — the rung above. */
export function railLevel(level: PlanLevel): 'season' | 'year' | null {
  if (level === 'month') return 'season'
  if (level === 'season') return 'year'
  return null
}

/**
 * The rail's OFFER list: which of the level above a session may still take
 * down. A row whose commitment to that period is already answered — carried
 * into the next one, dropped, done — is reference, not an offer; offering it
 * would write a second placement over a decision that has moved on. Only an
 * OPEN commitment (or a legacy row, which has no record to read) may be
 * taken. Goals are never offerable: they stay on their own level.
 */
export function offerableFromAbove(
  tasks: readonly Task[],
  level: 'month' | 'season',
  periodStart: Date,
  isCurrent: boolean,
  seasons: Seasons = readSeasons(),
): Task[] {
  return tasks.filter((t) => {
    if (t.isGoal) return false
    const c = committedTo(t, level, periodStart, { isCurrent, seasons })
    return c === 'legacy' || (c !== undefined && c.status === 'open')
  })
}

/**
 * What a season row's "Into a month…" offers: every month of the season, and
 * the month just BEFORE it — so a plan written on paper for Fall can still put
 * work into the September already under way (Scott, 2026-09-25). Choosing it
 * keeps the season commitment (moving down never closes a higher rung), so the
 * row stays on the season's list, marked "→ September". Across a year boundary
 * the earlier month names its year ("December 2026 (before Winter)").
 */
export function intoMonthChoices(bounds: Pick<PeriodBounds, 'start' | 'end' | 'label'>): { date: Date; label: string; beforeSeason: boolean }[] {
  const months: Date[] = []
  for (let d = new Date(bounds.start.getFullYear(), bounds.start.getMonth(), 1); d < bounds.end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) months.push(d)
  if (months.length === 0) return []
  const first = months[0]
  const before = new Date(first.getFullYear(), first.getMonth() - 1, 1)
  const name = (d: Date, withYear: boolean) => d.toLocaleDateString('en-US', withYear ? { month: 'long', year: 'numeric' } : { month: 'long' })
  const season = bounds.label.replace(/\s+\d{4}(\s*[–-]\s*\d{2,4})?$/, '')
  return [
    { date: before, label: `${name(before, before.getFullYear() !== first.getFullYear())} (before ${season})`, beforeSeason: true },
    ...months.map((d) => ({ date: d, label: name(d, false), beforeSeason: false })),
  ]
}
